import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BoardGrid from "../components/BoardGrid";
import SentenceTrack from "../components/SentenceTrack";
import { isFirebaseConfigured } from "../firebase";
import { getCurrentUid } from "../game/rooms";
import {
  defaultColorForBoard,
  generateCodeId,
  loadCustomCodes,
  saveCustomCode,
  type CustomCode,
} from "../data/custom-codes";
import { getCell } from "../data/boards";
import type {
  FrameColor,
  GlossaryEntry,
  IconRef,
  SentenceToken,
  Tense,
  Gender,
} from "../game/types";
import { iconKey } from "../game/types";
import { supportedLanguages, type Lang } from "../i18n";
import {
  getStoredKey,
  setStoredKey,
  suggestCode,
  suggestedTokensToSentenceTokens,
} from "../game/gemini";

const POS_OPTIONS: FrameColor[] = ["noun", "verb", "adj", "prep", "adverb", "neutral"];

interface Translations { en: string; it: string; de: string; fr: string; }

export default function CodeEditor() {
  const { codeId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tokens, setTokens] = useState<SentenceToken[]>([]);
  const [translations, setTranslations] = useState<Translations>({ en: "", it: "", de: "", fr: "" });
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authorName, setAuthorName] = useState(() => localStorage.getItem("lexikon.displayName") ?? "");
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Gemini suggestion state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiKey, setAiKey] = useState(() => getStoredKey());
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  async function runSuggest() {
    if (!aiPrompt.trim() || !aiKey.trim()) return;
    setAiBusy(true);
    setAiError(null);
    try {
      setStoredKey(aiKey.trim());
      const { tokens: suggested, translation } = await suggestCode({
        targetSentence: aiPrompt.trim(),
        apiKey: aiKey.trim(),
      });
      const newTokens = suggestedTokensToSentenceTokens(suggested);
      if (newTokens.length === 0) {
        setAiError("Gemini didn't return any usable icons. Try rephrasing.");
        return;
      }
      setTokens(newTokens);
      setTranslations((prev) => ({ ...prev, en: translation || aiPrompt.trim() }));
      setActiveIdx(0);
      setAiOpen(false);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  }

  // Load existing custom code when editing.
  useEffect(() => {
    if (!codeId || !isFirebaseConfigured()) return;
    setLoading(true);
    loadCustomCodes()
      .then((codes) => {
        const existing = codes.find((c) => c.id === codeId);
        if (existing) {
          setTokens(existing.tokens);
          setTranslations({
            en: existing.translations.en ?? "",
            it: existing.translations.it ?? "",
            de: existing.translations.de ?? "",
            fr: existing.translations.fr ?? "",
          });
          setEditingId(existing.id);
        }
      })
      .finally(() => setLoading(false));
  }, [codeId]);

  function addIcon(ref: IconRef) {
    setTokens((prev) => [
      ...prev,
      {
        icon: ref,
        order: prev.length + 1,
        frameColor: defaultColorForBoard(ref.board),
      },
    ]);
    setActiveIdx(tokens.length);
  }

  function updateToken(idx: number, patch: Partial<SentenceToken>) {
    setTokens((prev) => prev.map((tok, i) => (i === idx ? { ...tok, ...patch } : tok)));
  }

  function removeToken(idx: number) {
    setTokens((prev) => prev.filter((_, i) => i !== idx).map((t, i) => ({ ...t, order: i + 1 })));
    if (activeIdx === idx) setActiveIdx(null);
  }

  function moveToken(idx: number, delta: number) {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= tokens.length) return;
    const next = [...tokens];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setTokens(next.map((t, i) => ({ ...t, order: i + 1 })));
    setActiveIdx(newIdx);
  }

  function buildGlossary(): GlossaryEntry[] {
    return tokens.map((tok) => {
      const cell = getCell(tok.icon.board, tok.icon.col, tok.icon.row);
      return {
        iconKey: iconKey(tok.icon),
        pos: tok.frameColor,
        tense: tok.tense,
        negation: tok.negation,
        plural: tok.plural,
        gloss: {
          en: cell?.gloss?.en ?? "",
          it: cell?.gloss?.it ?? "",
          de: cell?.gloss?.de ?? "",
          fr: cell?.gloss?.fr ?? "",
        },
      };
    });
  }

  async function handleSave() {
    if (!authorName.trim()) {
      setError("Add a display name first (top of the panel).");
      return;
    }
    if (tokens.length === 0) {
      setError("Add at least one icon by clicking on the boards.");
      return;
    }
    if (!translations.en.trim()) {
      setError("English translation is required (other languages optional).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      localStorage.setItem("lexikon.displayName", authorName.trim());
      const uid = await getCurrentUid();
      const id = editingId ?? generateCodeId();
      const code: CustomCode = {
        id,
        iconCount: tokens.length,
        tokens,
        translations: {
          en: translations.en.trim(),
          it: translations.it.trim() || translations.en.trim(),
          de: translations.de.trim() || translations.en.trim(),
          fr: translations.fr.trim() || translations.en.trim(),
        },
        glossary: buildGlossary(),
        authorUid: uid,
        authorName: authorName.trim(),
        createdAt: editingId ? Date.now() : Date.now(),
        updatedAt: Date.now(),
      };
      await saveCustomCode(code);
      navigate("/codes");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const activeToken = activeIdx != null ? tokens[activeIdx] : null;

  if (!isFirebaseConfigured()) {
    return <div className="card">The codebook editor needs Firebase configured.</div>;
  }
  if (loading) return <div className="card">{t("common.loading")}</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>{editingId ? "Edit code" : "New code"}</h2>
          <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>
            Click icons on the boards to add them to the sentence in reading order.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn ghost" onClick={() => setAiOpen(true)}>✨ Suggest with AI</button>
          <Link to="/codes" className="btn ghost">{t("common.back")}</Link>
        </div>
      </div>

      {aiOpen && (
        <div className="zoom-backdrop" onClick={() => setAiOpen(false)}>
          <div className="zoom-card" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <button className="zoom-close" onClick={() => setAiOpen(false)}>×</button>
            <h3 style={{ margin: 0 }}>Suggest a code with Gemini</h3>
            <p style={{ color: "var(--ink-muted)", fontSize: 13, margin: 0 }}>
              Describe the sentence you want in English. Gemini will pick icons and order them. You can edit the result before saving.
            </p>
            <label className="field">
              Target sentence (English)
              <input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. The young scientist dreams of a peaceful world"
                autoFocus
              />
            </label>
            <label className="field">
              Gemini API key
              <input
                type="password"
                value={aiKey}
                onChange={(e) => setAiKey(e.target.value)}
                placeholder="Get one at aistudio.google.com/apikey"
              />
            </label>
            <p style={{ color: "var(--ink-muted)", fontSize: 11, margin: 0 }}>
              ⚠ Key is stored in your browser localStorage and called directly from your client. Use a personal key for testing only — don't share this browser session publicly.
            </p>
            {aiError && (
              <div style={{ background: "#fde2e1", color: "#9b2520", padding: 8, borderRadius: 6, fontSize: 13 }}>{aiError}</div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn primary" onClick={runSuggest} disabled={!aiPrompt.trim() || !aiKey.trim() || aiBusy}>
                {aiBusy ? "Asking Gemini…" : "Suggest"}
              </button>
              <button className="btn ghost" onClick={() => setAiOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="composer">
        <div>
          <BoardGrid tokens={tokens} onCellClick={addIcon} />
        </div>
        <aside className="panel" style={{ display: "grid", gap: 14, alignContent: "start" }}>
          <label className="field">
            Author name
            <input value={authorName} onChange={(e) => setAuthorName(e.target.value)} placeholder="Ada" maxLength={32} />
          </label>

          <div>
            <h3 style={{ margin: "0 0 8px" }}>Sentence ({tokens.length} icons)</h3>
            <SentenceTrack tokens={tokens} showGloss />
            {tokens.length === 0 && (
              <p style={{ color: "var(--ink-muted)", fontSize: 13 }}>No icons yet — tap any icon on a board.</p>
            )}
          </div>

          {tokens.length > 0 && (
            <div>
              <h3 style={{ margin: "0 0 8px" }}>Tokens</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 4 }}>
                {tokens.map((tok, i) => {
                  const cell = getCell(tok.icon.board, tok.icon.col, tok.icon.row);
                  return (
                    <li
                      key={i}
                      onClick={() => setActiveIdx(i)}
                      style={{
                        display: "flex", gap: 6, alignItems: "center",
                        padding: "6px 8px", borderRadius: 6,
                        background: activeIdx === i ? "var(--panel-2)" : "transparent",
                        cursor: "pointer",
                        border: activeIdx === i ? "1px solid var(--accent)" : "1px solid transparent",
                      }}
                    >
                      <span style={{ fontFamily: "monospace", fontWeight: 800, color: "var(--ink-muted)" }}>{tok.order}.</span>
                      <span style={{ fontFamily: "monospace", fontSize: 12 }}>{iconKey(tok.icon)}</span>
                      <span style={{ flex: 1, fontSize: 12 }}>{cell?.gloss?.en ?? "—"}</span>
                      <button className="btn ghost" style={{ padding: "2px 6px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); moveToken(i, -1); }} disabled={i === 0}>↑</button>
                      <button className="btn ghost" style={{ padding: "2px 6px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); moveToken(i, 1); }} disabled={i === tokens.length - 1}>↓</button>
                      <button className="btn ghost" style={{ padding: "2px 6px", fontSize: 11, color: "var(--danger)" }} onClick={(e) => { e.stopPropagation(); removeToken(i); }}>✕</button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {activeToken && activeIdx != null && (
            <div style={{ background: "var(--panel-2)", padding: 12, borderRadius: 8 }}>
              <h3 style={{ margin: "0 0 8px" }}>Token #{activeIdx + 1} — {iconKey(activeToken.icon)}</h3>
              <label className="field">
                Part of speech
                <select value={activeToken.frameColor} onChange={(e) => updateToken(activeIdx, { frameColor: e.target.value as FrameColor })}>
                  {POS_OPTIONS.map((p) => <option key={p} value={p}>{t(`pos.${p}`)}</option>)}
                </select>
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center", padding: "4px 0" }}>
                <input type="checkbox" checked={!!activeToken.plural} onChange={(e) => updateToken(activeIdx, { plural: e.target.checked })} />
                Plural (double border)
              </label>
              {activeToken.frameColor === "verb" && (
                <label className="field">
                  Tense
                  <select value={activeToken.tense ?? ""} onChange={(e) => updateToken(activeIdx, { tense: (e.target.value || undefined) as Tense | undefined })}>
                    <option value="">(no tense)</option>
                    <option value="past">Past</option>
                    <option value="present">Present</option>
                    <option value="future">Future</option>
                  </select>
                </label>
              )}
              <label className="field">
                Gender (optional)
                <select value={activeToken.gender ?? ""} onChange={(e) => updateToken(activeIdx, { gender: (e.target.value || undefined) as Gender | undefined })}>
                  <option value="">(none)</option>
                  <option value="M">Male ♂</option>
                  <option value="F">Female ♀</option>
                  <option value="NB">Non-binary ⚧</option>
                </select>
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center", padding: "4px 0" }}>
                <input type="checkbox" checked={!!activeToken.negation} onChange={(e) => updateToken(activeIdx, { negation: e.target.checked })} />
                Negation ✕ (or select X-image on split cells)
              </label>
              {activeIdx < tokens.length - 1 && (
                <label style={{ display: "flex", gap: 6, alignItems: "center", padding: "4px 0" }}>
                  <input type="checkbox" checked={!!activeToken.compoundWithNext} onChange={(e) => updateToken(activeIdx, { compoundWithNext: e.target.checked })} />
                  Compound (+) — join with next icon as one word
                </label>
              )}
            </div>
          )}

          <div>
            <h3 style={{ margin: "0 0 8px" }}>Translations</h3>
            {supportedLanguages.map((lng) => (
              <label className="field" key={lng}>
                {lng.toUpperCase()}{lng === "en" ? " *" : ""}
                <input
                  value={translations[lng]}
                  onChange={(e) => setTranslations({ ...translations, [lng]: e.target.value })}
                  placeholder={lng === "en" ? "Required" : "Optional — defaults to EN"}
                />
              </label>
            ))}
          </div>

          {error && (
            <div style={{ background: "#fde2e1", color: "#9b2520", padding: 8, borderRadius: 6, fontSize: 13 }}>{error}</div>
          )}

          <button className="btn primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editingId ? "Update code" : "Save new code"}
          </button>
        </aside>
      </div>
    </div>
  );
}
